import { useEffect, useRef, useState, type ReactNode } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, ImagePlus, Italic, List, ListOrdered, Loader2 } from 'lucide-react';
import { isEmptyTicketHtml } from '../lib/cockpitTickets';
import { fileToDataUrl } from '../lib/ticketScreenshots';

const MAX_INLINE_IMAGES = 4;

type Props = {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
};

function imageCount(html: string): number {
  return html.match(/<img\b/gi)?.length ?? 0;
}

export function TicketDetailsEditor({ value, onChange, disabled = false }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<Editor | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function insertImage(file: File) {
    const current = editorRef.current;
    if (!current) return;
    if (imageCount(current.getHTML()) >= MAX_INLINE_IMAGES) {
      setError(`You can place up to ${MAX_INLINE_IMAGES} images in the text.`);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const src = await fileToDataUrl(file);
      const alt = file.name.replace(/\.[^.]+$/, '') || 'Image';
      current.chain().focus().setImage({ src, alt }).run();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the image');
    } finally {
      setBusy(false);
    }
  }

  const insertRef = useRef(insertImage);
  insertRef.current = insertImage;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      Image.configure({ inline: false, allowBase64: true }),
      Placeholder.configure({
        placeholder: 'What happened, and what you expected. Paste an image into the text if it helps.',
      }),
    ],
    content: value || '',
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'ticket-rich-text ticket-details-editor min-h-[8.5rem] px-3 py-2 text-sm text-slate-900 focus:outline-none',
        'data-ticket-details-editor': 'true',
      },
      handlePaste(_view, event) {
        const file = [...(event.clipboardData?.files ?? [])].find((item) => item.type.startsWith('image/'));
        if (!file) return false;
        event.preventDefault();
        void insertRef.current(file);
        return true;
      },
    },
    onUpdate: ({ editor: current }) => {
      onChangeRef.current(current.getHTML());
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled && !busy);
  }, [editor, disabled, busy]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value === current) return;
    if (isEmptyTicketHtml(value) && isEmptyTicketHtml(current)) return;
    editor.commands.setContent(value || '', { emitUpdate: false });
  }, [value, editor]);

  return (
    <div
      data-ticket-details-editor
      className={[
        'overflow-hidden rounded-lg border border-slate-300/90 bg-white shadow-sm',
        disabled ? 'opacity-70' : '',
      ].join(' ')}
    >
      <div className="flex items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-1.5 py-1">
        <ToolbarButton
          label="Bold"
          disabled={disabled || busy}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <Bold className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          disabled={disabled || busy}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          label="Bulleted list"
          disabled={disabled || busy}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <List className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          disabled={disabled || busy}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          label="Add image to the text"
          disabled={disabled || busy}
          onClick={() => fileInputRef.current?.click()}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <ImagePlus className="h-3.5 w-3.5" aria-hidden />}
        </ToolbarButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="sr-only"
          disabled={disabled || busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void insertImage(file);
          }}
        />
      </div>
      <EditorContent editor={editor} />
      {error && (
        <p className="border-t border-red-100 bg-red-50 px-3 py-1.5 text-xs text-red-800" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function ToolbarButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-600 hover:bg-white hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
